<script lang="ts">
import { postReaction, type Session } from '../services';
import OpenChatRoom from './OpenChatRoom.svelte';
import LivePoll from '../LivePoll.svelte';
import ReactionRiver from './ReactionRiver.svelte';
import { markdown } from '../util';
import throttle from 'lodash.throttle';
import CircularProgress from './CircularProgress.svelte';
import { user } from '../store';
import { onDestroy } from 'svelte';
import * as config from '../config.json';
import IconChat from './Icons/IconChat.svelte';
import IconBullhorn from './Icons/IconBullhorn.svelte';
import { getChannel } from './sendbird';
import { filter } from 'rxjs';
import IconClap from './Icons/IconClap.svelte';
import IconCheers from './Icons/IconCheers.svelte';
import IconHeart from './Icons/IconHeart.svelte';

export let sectionTitle;
export let session: Session;
export let isPast : boolean;

const reactions = {
  'c': IconClap,
  'r': IconCheers,
  'h': IconHeart,
}

let error;
let showChat = false;
let river;
let askQuestionChannel;
let presentQuestionDialog = false;
let question = '';
let closeQuestionTimer;
let channelSubscription;
let presentGetUsername = false;
let sendbird;

enum QuestionState {
  Idle,
  Sending,
  Sent
}
let questionState = QuestionState.Idle;

const throttledPostReaction = throttle(postReaction, 500, {trailing: false})
function sendReaction(emoji) {
  throttledPostReaction(config.eventId, session.id, emoji);
  river.add(emoji, true);
}

function updateShow() {
  channelSubscription?.unsubscribe();
  if (session.questionChannel) {
    channelSubscription = getChannel(session.questionChannel).pipe(filter(x=>x)).subscribe(data => {
      sendbird = data.sendbird;
      askQuestionChannel = data.channel;
    });
  }
}

function closeQuestionDialog() {
  clearTimeout(closeQuestionTimer);
  presentQuestionDialog = false;
  question = ''
  questionState = QuestionState.Idle;
}

function sendQuestion(e) {
  e.preventDefault();

  const quest = question.trim();
  if (quest.length === 0) return;

  questionState = QuestionState.Sending;

  const params = new sendbird.UserMessageParams();
  params.message = `(${session.name}) ${quest}`;
  params.pushNotificationDeliveryOption = 'suppress';
  askQuestionChannel.sendUserMessage(params, (userMessage, error) => {
    if (!error) {
      question = ''
      questionState = QuestionState.Sent;

      closeQuestionTimer = setTimeout(closeQuestionDialog, 2000)
    } else {
      console.error(error)
      closeQuestionDialog()
    }
  });
}

onDestroy(() => {
  clearTimeout(closeQuestionTimer);
  channelSubscription?.unsubscribe();
})

function toggleChatRoom() {
  if (!showChat && $user.name.trim().length === 0) {
    presentGetUsername = true;
    return;
  }

  showChat = !showChat
}

function saveNameForChat() {
  presentGetUsername = false;
  $user.name = $user.name.trim()
  if ($user.name.length === 0) return;
  showChat = true;
}

$: session, updateShow()
</script>

<div class="row">
  <div class="section-header">{sectionTitle}</div>
</div>

<div class="rowtocol">
  <div class="sixteen-nine flex-1" style="max-height: 70vh">
    <iframe
      title="show"
      src={isPast ? session.replayStream : session.liveStream}
      width="100%"
      height="100%"
      frameborder="0"
      scrolling="no"
      allow="autoplay"
      allowfullscreen="true"
      webkitallowfullscreen="true"
      mozallowfullscreen="true"
      oallowfullscreen="true"
      msallowfullscreen="true"
      class="content" />

    {#if session.reactions}
      <div class="reaction-emitter">
        <ReactionRiver bind:this={river} eventId={config.eventId} sessionId={session.id} --color="var(--secondary)" />
      </div>
    {/if}

  </div>
  {#if session.chatChannel && !isPast && showChat}
    <div class="chatRoomBox col">
      <div class="topbar row vcenter between">
        <div class="topbar-button control-button row vcenter hcenter" on:click={toggleChatRoom}>
          <span class="material-icons" on:click={() => showChat = false}>close</span>
        </div>

        Live Chat

        <div style="width: 42px" />
      </div>
      <OpenChatRoom url={session.chatChannel} />
    </div>
  {/if}
</div>

<LivePoll showId={session.id} />

<div class="title-box rowtocol-tablet vcenter between">
  <div class="row vcenter flex-1">
    <!-- <div class="whitebox" /> -->
    <!-- <div class="logo" /> -->
    <!-- <div class="slant mr-2" /> -->
    <div class="title-text-1">{session.name}</div>
  </div>

  <div class="row vcenter" style="gap: 32px">
    {#if askQuestionChannel && !isPast}
      <div class="col hcenter">
        <div class='control-button col vcenter hcenter' on:click={() => presentQuestionDialog = true}>
          <IconBullhorn --color="var(--secondary)" />
        </div>
        <div class="control-label">Ask a Question</div>
      </div>
    {/if}
    
    {#if session.reactions}
      <div class="col hcenter">
        <div class="row">
          {#each Object.entries(reactions) as [key, comp]}
            <div class="control-button col vcenter hcenter" style="--color:var(--secondary)" on:click={() => sendReaction(key)}>
              <svelte:component this={comp} />
            </div>
          {/each}
        </div>
        <div class="control-label">Reactions</div>
      </div>
    {/if}

    {#if session.chatChannel && !isPast}
      <div class="col hcenter">
        <div class="control-button col vcenter hcenter" on:click={toggleChatRoom}>
          <IconChat --color="var(--secondary)" />
        </div>
        <div class="control-label">Chat</div>
      </div>
    {/if}
  </div>
</div>

<div class="about-box col">
  <div class="about-title">ABOUT</div>
  <div class="sub-title">{session.name}</div>
  <div class="desc">{@html markdown(session.description)}</div>
</div>

{#if presentQuestionDialog}
  <div class="modal">
    <div class="dialog-container col" class:vcenter={questionState === QuestionState.Sent}>

      {#if questionState !== QuestionState.Sent}
        <div class='row between vcenter mb-2'>
          <div class="dialog-title">Ask a Question</div>
          <div class="control-button col vcenter hcenter" on:click={closeQuestionDialog}><i class="material-icons">close</i></div>
        </div>
        <div class="dialog-label">Question:</div>
        <textarea class="question-text mb-2" bind:value={question} disabled={questionState === QuestionState.Sending} />
        {#if questionState === QuestionState.Sending}
          <CircularProgress />
        {:else}
          <div class="button btn" style="align-self: flex-end" on:click={sendQuestion}>Submit Question</div>
        {/if}
      {:else}
        <div class="question-sent">Sent!</div>
      {/if}
    </div>
  </div>
{/if}

{#if presentGetUsername}
  <div class="modal">
    <div class="dialog-container-chat col">
      <div class='row between vcenter mb-2'>
        <div class="dialog-title">Name</div>
        <div class="control-button col vcenter hcenter" on:click={() => presentGetUsername = false}><i class="material-icons">close</i></div>
      </div>
      <div class="dialog-label">Please enter your first and last name for chat</div>
      <input class="mb-2" bind:value={$user.name} on:keydown={e => e.key === 'Enter' && saveNameForChat()} type='text' />
      <div class="button btn" style="align-self: flex-end" on:click={saveNameForChat}>OK</div>
    </div>
  </div>
{/if}

<style lang="scss">
@import '../util';

.dialog-container, .dialog-container-chat {
  width: 400px;
  height: 250px;
  border-radius: 5px;
  background-color: var(--primary);
  color: var(--text-primary);
  @include shadow;
  padding: 8px 16px;
}

.dialog-container-chat {
  height: unset;
}

.dialog-title {
  font-size: 24px;
}

.dialog-label {
  font-size: 18px;
}

.question-text {
  width: 100%;
  height: 80px;
  font-family: inherit;
  color: black;
}

.question-sent {
  align-self: center;
  font-size: 26px;
  font-weight: bold;
}

.control-label {
  user-select: none;
  font-size: 14px;
  color: var(--text-primary);
}

.reaction-emitter {
  pointer-events: none;
  bottom: 35px;
  right: 180px;
  position: absolute;
  z-index: 1000;
}
.chatRoomBox {
  display: flex;
  border: 1px solid rgba(0,0,0,.1);
  min-width: 280px;
  background-color: #f7f7f8;

  @include mobile {
    height: 40vh;
  }
}

.topbar {
  height: 42px;
  // border-bottom: 1px solid rgba(0,0,0,.1);
  background-color: var(--primary);
  color: var(--text-primary);
}

.topbar-button {
  background-color: var(--secondary);
  color: var(--text-secondary);
  border-radius: 0 !important;
}

$control-button-size: 42px;

.control-button {
  user-select: none;
  cursor: pointer;
  border-radius: 5px;
  width: $control-button-size;
  height: $control-button-size;
  &:hover {
    background-color: rgba(255,255,255,.2);
  }
}

.title-box {
  background-color: var(--primary);
  color: var(--text-primary);
  font-size: 32px;
  padding: 16px 32px;
  flex-shrink: 0;
  @include shadow;
}

.whitebox {
  width: 12px;
  height: 100%;
  background-color: white;
}

.title-text-1 {
  // white-space: nowrap;
  font-size: 24px;
  font-weight: 900;
  max-width: 700px;
  margin-right: 48px;

  @include mobile {
    display: none;
  }
}

.about-box {
  @include shadow;
  background-color: white;
  color: black;
  margin: 16px 0;
  padding: 16px 32px;
}

.about-title {
  margin-bottom: 8px;
  font-size: 20px;
  font-weight: bold;

  @include mobile {
    display: none;
  }
}

.sub-title {
  margin-bottom: 8px;
  font-weight: bold;
  font-size: 16px;
}

.desc {
  font-size: 16px;
  font-weight: normal;
}

.sixteen-nine {
  @include aspect-ratio(16, 9);
  background-color: black;
  overflow: hidden;
}
</style>